import Actions from '../../redux/Actions';
import Config from '../../globals/Config';
import Store from '../../redux/Store';
import HttpService from '../HttpService/HttpService';
import Constants, {DashboardFilter, PrivacyTypes, SortMetric} from '../../globals/Constants';
import UserService from '../UserService/UserService';
import CommentModel from '../../types/CommentModel';
import ReviewModel from '../../types/ReviewModel';
import ReviewRequestModel from '../../types/ReviewRequestModel';
import ReviewerModel from '../../types/ReviewerModel';
import ReplyRequestModel from '../../types/ReplyRequestModel';
import ReduxState from '../../types/ReduxState';
import CommentRequestModel from '../../types/CommentRequestModel';
import AxiosCancelTokenHelper from '../../utils/AxiosCancelTokenHelper/AxiosCancelTokenHelper';
import ReviewerRoleRequestModel from '../../types/ReviewerRoleRequestModel';
import AddNewVersionModel from '../../types/AddNewVersionModel';
import ReviewVersionModel from '../../types/ReviewVersionModel';
import {downloadReviewCsv} from '../CsvGenerator/CsvGenerator';
import UpdateReviewModel from '../../types/UpdateReviewModel';

const baseUrl = Config.apiEndPoint + Config.apiPrefix;

const getFormattedComment = (comment: any, timeInMs: number, reviewId: string, isResolved: boolean, resolutionDate: string, parentCommentId: string, replies: CommentModel[] = []): CommentModel => {
   const {Reactions, AuthorId, Text, Id, CreateDate, LastModifiedDate, IsDeleted, Annotation, SynchTimeStamp} = comment;

   let likes = [];
   if (Reactions) {
      likes = Reactions.filter((reaction: any) => reaction.TypeName === Constants.reactionTypes.like).map((reaction: any) => ({userId: reaction.UserId}));
   }

   return {
      authorId: AuthorId,
      text: Text,
      id: Id,
      createDate: CreateDate,
      lastModifiedDate: LastModifiedDate,
      time: timeInMs / 1000,
      reviewId: reviewId,
      isDeleted: IsDeleted,
      likes: likes,
      annotation: Annotation,
      isResolved: isResolved,
      resolutionDate: resolutionDate,
      synchTimeStamp: SynchTimeStamp,
      parentCommentId: parentCommentId,
      replies: replies.map(reply => getFormattedComment(reply, timeInMs, reviewId, isResolved, resolutionDate, comment.Id, []))
   };
};

const decorateReviews = (reviews: ReviewModel[]): void => {
   reviews.forEach(review => {
      if (review.RequestDate) {
         review.RequestDate = new Date(review.RequestDate);
      }

      if (review.ReviewByDate) {
         review.ReviewByDate = new Date(review.ReviewByDate);
      }

      review.LatestVersionNumber = review.ReviewVersions.length;

      review.PrivacyType = review.PrivacyType.toLowerCase() as PrivacyTypes;
   });
};

const loadReview = async (reviewId: string, versionNumber: number): Promise<ReviewVersionModel> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithMultipleAuths(`${baseUrl}/reviews/${reviewId}`), `LoadReview-${reviewId}`, Constants.statusCodes.ok);
   const review: ReviewModel = response.data;
   const latestVersionNumber = review.ReviewVersions.filter(ver => !ver.IsDeleted).pop().VersionNumber;
   review.ReviewVersions = response.data.ReviewVersions;
   decorateReviews([review]);
   Store.dispatch(Actions.addOrUpdateReview(review));

   if (versionNumber > latestVersionNumber) {
      throw {message: Constants.invalidVersionNumberError};
   }

   Store.dispatch(Actions.setReviewId(reviewId, versionNumber));
   return review.ReviewVersions.filter(ver => ver.VersionNumber === versionNumber)[0];
};

const getSortMetricNumber = (sortMetric: SortMetric): number => {
   switch (sortMetric) {
      case SortMetric.titleAscending:
         return 1;
      case SortMetric.titleDescending:
         return 2;
      case SortMetric.createdDateAscending:
         return 3;
      case SortMetric.createdDateDescending:
         return 4;
      default:
         return null;
   }
};

const getDashboardFilterNumber = (dashboardFilter: DashboardFilter): number => {
   switch (dashboardFilter) {
      case DashboardFilter.all:
         return 1;
      case DashboardFilter.owned:
         return 2;
      case DashboardFilter.others:
         return 3;
      default:
         return null;
   }
};

const shouldLoadMoreReviews = (newReviews: ReviewModel[], dashboardFilter: DashboardFilter): boolean => {
   if (!(dashboardFilter in DashboardFilter) || dashboardFilter === DashboardFilter.all) {
      return false;
   }

   if (dashboardFilter === DashboardFilter.owned && newReviews.some(r => r.Reviewers.some(reviewer => reviewer.Id === Config.user.techSmithId && reviewer.Role === Constants.reviewerRoles.owner))) {
      return false;
   }

   if (dashboardFilter === DashboardFilter.others && newReviews.some(r => r.Reviewers.some(reviewer => reviewer.Id === Config.user.techSmithId && reviewer.Role === Constants.reviewerRoles.reviewer) && !r.Reviewers.some(reviewer => reviewer.Id === Config.user.techSmithId && reviewer.Role === Constants.reviewerRoles.owner))) {
      return false;
   }

   if (newReviews.length === 0) {
      return false;
   }

   return true;
};

const loadReviews = async (sortMetric: SortMetric, currentlyLoadedReviewsCount: number, remainingReviewsCount: number, dashboardFilter: DashboardFilter, axiosCancelTokenHelper?: AxiosCancelTokenHelper, isFirstLoad?: boolean): Promise<void> => {
   const sortMetricNumber = getSortMetricNumber(sortMetric);
   let remainingReviewsTotal = remainingReviewsCount;
   let skipValue = currentlyLoadedReviewsCount;
   let takeValue = remainingReviewsTotal < Constants.initialTakeValue ? remainingReviewsTotal : Constants.initialTakeValue;
   let reviews: ReviewModel[] = [];
   do {
      const queryParam = sortMetricNumber ? `?sortMetric=${sortMetricNumber}&skip=${skipValue}&take=${takeValue}&filter=${getDashboardFilterNumber(DashboardFilter.all)}` : '';
      const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithTscAuthToken(`${baseUrl}/reviews${queryParam}`, axiosCancelTokenHelper), 'LoadReviews', Constants.statusCodes.ok, axiosCancelTokenHelper);
      reviews = response.data.Responses;
      decorateReviews(reviews);
      Store.dispatch(Actions.batchAddReviews(reviews));
      Store.dispatch(Actions.setTotalReviewsCount(response.data.TotalOwnedReviewCount + response.data.TotalNotOwnedReviewCount));
      Store.dispatch(Actions.setTotalOwnedReviewsCount(response.data.TotalOwnedReviewCount));
      Store.dispatch(Actions.setTotalNotOwnedReviewsCount(response.data.TotalNotOwnedReviewCount));

      if (isFirstLoad) {
         const calculatedRemainingReviews = response.data.TotalOwnedReviewCount + response.data.TotalNotOwnedReviewCount;
         remainingReviewsTotal = calculatedRemainingReviews < takeValue ? 0 : calculatedRemainingReviews - takeValue;
      } else {
         remainingReviewsTotal = remainingReviewsTotal - takeValue;
      }

      skipValue = skipValue + takeValue;
      takeValue = remainingReviewsTotal < Constants.initialTakeValue ? remainingReviewsTotal : Constants.initialTakeValue;
   } while (shouldLoadMoreReviews(reviews, dashboardFilter));
};

const getReviewTitleById = (reviewId: string): string => {
   let reviewTitle = '';
   const {reviews}: ReduxState = Store.getState();
   reviews.forEach(review => {
      if (review.Id === reviewId) {
         reviewTitle = review.Title;
      }
   });
   return reviewTitle;
};

const downloadCsvData = async (reviewId: string): Promise<void> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithMultipleAuths(`${baseUrl}/reviews/${reviewId}/csv`), `DownloadCSV-${reviewId}`, Constants.statusCodes.ok);
   return downloadReviewCsv(response.data.Title, response.data.CsvRows);
};

const loadComments = async (version: ReviewVersionModel, newestLastModifiedDate?: string): Promise<void> => {
   const queryParam = newestLastModifiedDate ? `?newerThanLastModifiedDate=${encodeURIComponent(newestLastModifiedDate)}` : '';
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.getWithMultipleAuths(`${baseUrl}/reviews/${version.Id}/comments${queryParam}`), 'LoadComments', Constants.statusCodes.ok);
   const comments: CommentModel[] = [];

   response.data.Comments.forEach((feedback: any) => {
      const {Comment, Time, ReviewId, IsResolved, ResolutionDate, Replies} = feedback;
      comments.push(getFormattedComment(Comment, Time, ReviewId, IsResolved, ResolutionDate, null, Replies));
      if (Replies) {
         Replies.forEach((reply: any) => {
            comments.push(getFormattedComment(reply, Time, ReviewId, false, ResolutionDate, Comment.Id));
         });
      }
   });

   await UserService.setUsers(response.data.Contributors);
   Store.dispatch(Actions.setComments(comments, version));
};

const postVersion = async (id: string, version: AddNewVersionModel): Promise<ReviewVersionModel> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithTscAuthToken(`${baseUrl}/reviews/${id}/versions`, version), 'PostVersion', Constants.statusCodes.created);
   const newVersion: ReviewVersionModel = response.data;
   Store.dispatch(Actions.setReviewId(id, newVersion.VersionNumber));
   return newVersion;
};

const deleteVersion = async (reviewId: string, versionNumber: number): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithTscAuthToken(`${baseUrl}/reviews/${reviewId}/versions/${versionNumber}`), 'DeleteVersion', Constants.statusCodes.noContent);
   Store.dispatch(Actions.removeVersion(reviewId, versionNumber));
};

const postReview = async (review: ReviewRequestModel): Promise<string> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithTscAuthToken(`${baseUrl}/reviews`, review), 'PostReview', Constants.statusCodes.created);
   const newReview: ReviewModel = response.data;
   decorateReviews([newReview]);
   Store.dispatch(Actions.addOrUpdateReview(newReview));
   Store.dispatch(Actions.setReviewId(newReview.Id, newReview.LatestVersionNumber));
   return newReview.Id;
};

const updateReview = async (review: UpdateReviewModel): Promise<void> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/reviews/${review.Id}`, review), 'UpdateReview', Constants.statusCodes.ok);
   const updatedReview: ReviewModel = response.data;
   decorateReviews([updatedReview]);
   Store.dispatch(Actions.addOrUpdateReview(updatedReview));
};

const updateReviewers = async (reviewId: string, reviewerRoleRequest: ReviewerRoleRequestModel): Promise<ReviewerModel[]> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/reviews/${reviewId}/reviewers`, reviewerRoleRequest), 'UpdateReviewers', Constants.statusCodes.ok);
   const reviewers = (response.data as ReviewerModel[]).filter(newReviewer => newReviewer.Status !== Constants.reviewerServerStatus.failed);
   UserService.setUsers(reviewers);
   Store.dispatch(Actions.addReviewers(reviewId, reviewers));
   return reviewers;
};

const leaveReview = async (reviewId: string): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithTscAuthToken(`${baseUrl}/reviews/${reviewId}/reviewers/${Config.user.techSmithId}`), 'RemoveReviewer', Constants.statusCodes.noContent);
   Store.dispatch(Actions.leaveReview(reviewId));
};

const addComment = async (reviewId: string, comment: CommentRequestModel): Promise<CommentModel> => {
   const commentRequest = {
      annotation: comment.annotation,
      mediaTimeInMilliseconds: comment.time * 1000,
      text: comment.text,
      isResolved: false
   };
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithMultipleAuths(`${baseUrl}/reviews/${reviewId}/comments`, commentRequest), 'AddComment', Constants.statusCodes.created);
   const formattedComment = getFormattedComment(response.data.Comment, response.data.Time, response.data.ReviewId, response.data.IsResolved, response.data.ResolutionDate, null);
   Store.dispatch(Actions.addComment(formattedComment));
   return formattedComment;
};

const addReply = async (reply: ReplyRequestModel, parentComment: CommentModel): Promise<CommentModel> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithMultipleAuths(`${baseUrl}/comments/${parentComment.id}/replies`, reply), 'AddReply', Constants.statusCodes.created);
   const formattedComment = getFormattedComment(response.data, parentComment.time * 1000, parentComment.reviewId, false, parentComment.resolutionDate, parentComment.id);
   Store.dispatch(Actions.addComment(formattedComment));
   return formattedComment;
};

const resolveComment = async (comment: CommentModel): Promise<void> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/comments/${comment.id}/resolution`, {isResolved: true}), 'ResolveComment', Constants.statusCodes.ok);
   Store.dispatch(Actions.editComment(getFormattedComment(response.data.Comment, response.data.Time, response.data.ReviewId, response.data.IsResolved, response.data.ResolutionDate, null, response.data.Replies)));
};

const unresolveComment = async (comment: CommentModel): Promise<void> => {
   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithTscAuthToken(`${baseUrl}/comments/${comment.id}/resolution`, {isResolved: false}), 'UnresolveComment', Constants.statusCodes.ok);
   Store.dispatch(Actions.editComment(getFormattedComment(response.data.Comment, response.data.Time, response.data.ReviewId, response.data.IsResolved, response.data.ResolutionDate, null, response.data.Replies)));
};

const likeComment = async (comment: CommentModel, userId: string): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.postWithMultipleAuths(`${baseUrl}/comments/${comment.id}/like`), 'LikeComment', Constants.statusCodes.created);
   const updatedComment = Object.assign({}, comment);
   updatedComment.likes.push({userId});
   Store.dispatch(Actions.editComment(updatedComment));
};

const unlikeComment = async (comment: CommentModel, userId: string): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithMultipleAuths(`${baseUrl}/comments/${comment.id}/like`), 'UnlikeComment', Constants.statusCodes.noContent);
   const updatedComment = Object.assign({}, comment);
   updatedComment.likes = updatedComment.likes.filter(like => like.userId !== userId);
   Store.dispatch(Actions.editComment(updatedComment));
};

const deleteReview = async (reviewId: string): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithTscAuthToken(`${baseUrl}/reviews/${reviewId}`), 'DeleteReview', Constants.statusCodes.noContent);
   Store.dispatch(Actions.removeReview(reviewId));
};

const editComment = async (comment: CommentModel): Promise<void> => {
   const {text, annotation, id, time, authorId, isResolved, resolutionDate, createDate, reviewId, likes, parentCommentId, replies} = comment;

   const commentRequest = {
      text: text,
      annotation: annotation
   };

   const response = await HttpService.executeRequestWithErrorHandling(() => HttpService.putWithMultipleAuths(`${baseUrl}/comments/${id}`, commentRequest), 'EditComment', Constants.statusCodes.ok);
   const {Annotation, Text, IsDeleted, LastModifiedDate, SynchTimeStamp} = response.data;

   const updatedComment = {
      annotation: Annotation,
      time: time,
      authorId: authorId,
      text: Text,
      isDeleted: IsDeleted,
      isResolved: isResolved,
      resolutionDate: resolutionDate,
      id: id,
      lastModifiedDate: LastModifiedDate,
      createDate: createDate,
      reviewId: reviewId,
      likes: likes,
      parentCommentId: parentCommentId,
      synchTimeStamp: SynchTimeStamp,
      replies: replies
   };

   Store.dispatch(Actions.editComment(updatedComment));
};

const deleteComment = async (comment: CommentModel): Promise<void> => {
   await HttpService.executeRequestWithErrorHandling(() => HttpService.deleteWithMultipleAuths(`${baseUrl}/comments/${comment.id}`), 'DeleteComment', Constants.statusCodes.noContent);
   Store.dispatch(Actions.editComment(Object.assign({}, comment, {
      text: '',
      annotation: '',
      isDeleted: true,
      isResolved: false
   })));
};

export default {
   loadReview,
   loadReviews,
   getReviewTitleById,
   getSortMetricNumber,
   postReview,
   updateReview,
   updateReviewers,
   leaveReview,
   loadComments,
   addComment,
   addReply,
   deleteReview,
   editComment,
   likeComment,
   unlikeComment,
   deleteComment,
   resolveComment,
   unresolveComment,
   postVersion,
   downloadCsvData,
   deleteVersion
};
